import * as PropTypes from "prop-types";
import * as React from "react";
import { ScrollSync } from "react-virtualized/dist/commonjs/ScrollSync";
import { Grid } from "react-virtualized/dist/commonjs/Grid";
import { AutoSizer } from "react-virtualized/dist/commonjs/AutoSizer";
import itemViews from "components/items/views";
import Separator, { SortableSeparator } from "components/items/separator";
import Image, { SortableImage } from "components/items/image-separator";
import { $, merge } from "glamor";
import { formatMessage } from "lib/globalize-util";
import DragPreview from "components/items/views/preview";
import { ITEM_SEPARATOR, ITEM } from "constants/catalogs";
import CircularProgress from "material-ui/CircularProgress";
import type { IScope } from "reducers/scope";
import { getCatalogDates } from "components/items/grid";
import { MAX_SELECT } from "constants/shift-select-types";
import { generateSelectedIdArray } from "components/utils/utilities";
import { AccountType } from "types/user";
import { SelectionWhiteList } from "helpers/selection-state";
import { getNodeKey } from "components/items/items-utils";
import { getViewDef } from "helpers/view-def";
import { VIEW_PRODUCT_LIST } from "constants/view-defs";

const FALLBACK_ROW_WIDTH = 800;
const NO_RESULTS_GRID_HEIGHT = 120;

const getListForItems = ({ result, entities, sortingIndex, View, editable, draggingEntryId }) => {
    const grid = [];
    const { items = {} } = entities;

    for (let i = 0; i < result.length; i++) {
        const id = result[i];
        const item = items[id];

        grid.push({
            item,
            height: View.height,
            index: i,
        });
    }

    grid.unshift({
        item: null,
        height: View.height,
        index: 0,
    });

    return {
        grid,
        height: grid.length * View.height,
    };
};

const calculateHeight = (type) => {
    let height;
    switch (type) {
        case ITEM_SEPARATOR:
            height = 54;
            break;
        case "image":
            height = 330;
            break;
        case ITEM:
        default:
            height = 60;
    }

    return height;
};

const getListForCatalog = ({ result, entities, sortingIndex, View, editable, draggingEntryId }) => {
    const grid = [];
    const { items = {}, entries = {} } = entities;
    let height = 0;
    for (let i = 0; i < result.length; i++) {
        const id = result[i];
        const entry = entries[id];
        if (!entry) {
            continue;
        }
        const item = items[entry.item || entry.item_id];
        const isImageOrSeparator = entry.type === "image" || entry.type === ITEM_SEPARATOR;

        if (isImageOrSeparator) {
            grid.push({
                entry,
                item,
                height: calculateHeight(entry.type),
                index: i,
            });
            height += calculateHeight(entry.type);
        } else {
            grid.push({
                entry,
                item,
                height: View.height,
                index: i,
            });
            height += View.height;
        }
    }

    // add one to the end
    if (editable) {
        grid.push({
            height: View.height,
            index: result.length,
            placeholder: true,
        });
        height += View.height;
    }

    // add header row
    grid.unshift({
        entry: null,
        item: null,
        height: View.height,
        index: 0,
    });
    height += View.height;

    return { grid, height };
};

interface IProps {
    activeBrand: any;
    activeItemId: string;
    activeList: any;
    app: any;
    bulkItems: SelectionWhiteList;
    cart: any;
    catalog?: any;
    dragging: any;
    draggingEntryId: string;
    editable: boolean;
    entities: any;
    hasMoreResults: any;
    isActiveList: boolean;
    isFetching: boolean;
    items: any;
    list: boolean;
    pageSize: number;
    results: any;
    type: string;
    view: string;
    viewDef: any;
    viewDefs: any;
    viewNotes: any;
    scope: IScope;
    sortingIndex?: any;
    width?: any;
    bulkDragInsert?: boolean;
    bulkDragReorder?: boolean;
    onActiveListAdd: (itemId: string) => void;
    onActiveListFetchEntries: () => void;
    onActiveListRemove: () => void;
    onAddToSelectedItems: (items: any) => void;
    onClear: () => void;
    onDeleteEntry: (entry: any) => void;
    onDeleteSelectedEntries: (entries: any) => void;
    onDrag: () => void;
    onDrop: () => void;
    onSnackbarAdd: (message: any) => void;
    onFetchNextPage: () => void;
    onItemClick: () => void;
    onItemMoreInfo: () => void;
    onRemoveFromSelectedItems: (items: any) => void;
    onReorderEntries: () => void;
    onSetActiveList: (list: any) => void;
    onUpdateEntry: () => void;
    onViewNotes: () => void;
    onEntriesRefresh: () => void;
    noContentRenderer: () => React.ReactNode;
    toolbarMinimized: boolean;
    showActions: boolean;
}

interface IState {
    viewDef: any;
    grid: any;
    height: number;
    View: any;
    estimatedRows: number;
    groupSelectStartValue: string;
    shiftKey: boolean;
}

export class ItemsList extends React.Component<IProps, IState> {
    static contextTypes = {
        me: PropTypes.object.isRequired,
    };

    static propTypes = {
        actions: PropTypes.object,
        activeBrand: PropTypes.object.isRequired,
        activeItemId: PropTypes.string,
        activeList: PropTypes.object,
        app: PropTypes.object.isRequired,
        cart: PropTypes.object.isRequired,
        editable: PropTypes.bool,
        entities: PropTypes.object,
        isActiveList: PropTypes.bool,
        onDrag: PropTypes.func,
        onDrop: PropTypes.func,
        pageSize: PropTypes.number.isRequired,
        results: PropTypes.array.isRequired,
        onSnackbarAdd: PropTypes.func.isRequired,
        view: PropTypes.oneOf(["list"]),
        viewNotes: PropTypes.bool,
        noContentRenderer: PropTypes.func,
        overrideStyles: PropTypes.object,
        scope: PropTypes.object.isRequired,
        onEntriesRefresh: PropTypes.func,
    };

    static defaultProps = {
        view: "list",
        editable: false,
        isActiveList: false,
        entities: {},
        showActions: true,
    };

    itemActions: any;

    constructor(props) {
        super(props);
        this.state = {
            viewDef: undefined,
            grid: [],
            height: 0,
            View: undefined,
            estimatedRows: 0,
            groupSelectStartValue: null,
            shiftKey: false,
        };
    }

    UNSAFE_componentWillMount() {
        const { editable, view } = this.props;
        const View = typeof view === "string" ? itemViews[view][editable ? 1 : 0] : view;
        this.computeList(this.props, View);

        this.itemActions = {
            onItemClick: this.props.onItemClick,
            onBulkItemSelectionChange: this.handleBulkItemSelectionChange.bind(this),
            onDelete: this.props.onDeleteEntry,
            onDeleteSelectedEntries: this.props.onDeleteSelectedEntries,
            onUpdate: this.props.onUpdateEntry,
            onViewNotes: this.props.onViewNotes,
            onActiveListAdd: this.props.onActiveListAdd,
            onActiveListRemove: this.props.onActiveListRemove,
            onActiveListFetchEntries: this.props.onActiveListFetchEntries,
            onSetActiveList: this.props.onSetActiveList,
        };

        this.loadViewDef();

        this.setState({
            View,
        });

        window.addEventListener("scroll", this.handleWindowScrollEvent, false);
    }

    componentDidMount() {
        document.addEventListener("keydown", this.handleOnKeyDown);
        document.addEventListener("keyup", this.handleOnKeyUp);
    }

    componentWillUnmount() {
        window.removeEventListener("scroll", this.handleWindowScrollEvent, false);
        document.removeEventListener("keydown", this.handleOnKeyDown);
        document.removeEventListener("keyup", this.handleOnKeyUp);
    }

    componentDidUpdate(prevProps, prevState) {
        if (
            prevProps.sortingIndex !== this.props.sortingIndex ||
            prevState.View !== this.state.View ||
            prevState.viewDef !== this.state.viewDef ||
            prevProps.dragging !== this.props.dragging ||
            this.props.isFetching !== prevProps.isFetching
        ) {
            this.handleWindowScrollEvent();
        }
    }

    UNSAFE_componentWillReceiveProps(nextProps) {
        // switching brands resets grid viewDef
        if (
            (nextProps?.scope?.brand?._id && this.props?.scope?.brand?._id !== nextProps.scope.brand._id) ||
            nextProps.viewDef.result !== this.props.viewDef.result
        ) {
            this.loadViewDef(nextProps);
        }

        let View = this.state.View;

        if (nextProps.view !== this.props.view) {
            View =
                typeof nextProps.view === "string"
                    ? itemViews[nextProps.view][nextProps.editable ? 1 : 0]
                    : nextProps.view;
            this.setState({ View });
        }

        // see if the grid needs updating
        if (
            nextProps.width !== this.props.width ||
            this.props.isFetching !== nextProps.isFetching ||
            this.props.sortingIndex !== nextProps.sortingIndex ||
            this.props.activeList !== nextProps.activeList ||
            this.props.results !== nextProps.results ||
            this.props.entities.entries !== nextProps.entities.entries ||
            nextProps.view !== this.props.view ||
            nextProps.dragging !== this.props.dragging
        ) {
            this.computeList(nextProps, View);
        }
    }

    loadViewDef(props = this.props) {
        const { scope, viewDef: viewDefIds, viewDefs } = props;

        const viewDef = getViewDef({
            viewName: VIEW_PRODUCT_LIST,
            ids: viewDefIds.result,
            entries: viewDefs,
            schema: scope.brand.getSchema("product"),
        });
        if (viewDef) {
            this.setState({ viewDef });
        }
    }

    computeList(props, View) {
        const { results, entities, type, sortingIndex, editable, draggingEntryId } = props;

        const result = (type === "catalog" || editable ? getListForCatalog : getListForItems)({
            result: results,
            entities,
            sortingIndex,
            editable,
            View,
            draggingEntryId,
        });

        this.setState({
            grid: result.grid,
            height: result.height,
            estimatedRows: result.grid.length,
        });
    }

    handleOnKeyDown = (e) => {
        const { bulkItems } = this.props;
        const { groupSelectStartValue } = this.state;
        const bulkItemsArray = bulkItems.toArray();
        const bulkItemsArrayLength = bulkItemsArray.length;

        if (!groupSelectStartValue && bulkItemsArrayLength > 0) {
            this.setState({
                shiftKey: e.shiftKey,
                groupSelectStartValue: bulkItemsArray[bulkItemsArrayLength - 1] as string,
            });
        } else {
            this.setState({ shiftKey: e.shiftKey });
        }
    };

    handleOnKeyUp = () => {
        this.setState({ shiftKey: false });
    };

    handleBulkItemSelectionChange = (e, checked) => {
        const { onAddToSelectedItems, onRemoveFromSelectedItems, bulkItems, results, onSnackbarAdd, entities } =
            this.props;
        const { shiftKey, groupSelectStartValue } = this.state;
        const { entries } = entities;
        const bulkItemsLength = bulkItems.size;
        const selectionStartValue = e.target.value;

        // We only want to do the ShiftKey work if we have at least one item in bulkSelect
        if (shiftKey && bulkItemsLength > 0) {
            const selectedIds = generateSelectedIdArray(groupSelectStartValue, selectionStartValue, results);
            const bulkItemsArray = bulkItems.toArray();
            const uniqueItems = bulkItemsArray.concat(
                selectedIds.filter((item) => bulkItemsArray.indexOf(item.toString()) < 0),
            );
            const cleanItems = entries
                ? uniqueItems.filter((id: string) => !entries[id] || (entries[id] && entries[id].type === ITEM))
                : uniqueItems;

            if (cleanItems.length <= MAX_SELECT) {
                cleanItems.map((id) => onAddToSelectedItems(id));
            } else {
                // pop a toast imploring folks to select fewer than MAX_SELECT
                onSnackbarAdd({
                    message: formatMessage("Please limit selection to no more than |MAX_SELECT| items", { MAX_SELECT }),
                });
            }
        } else {
            if (checked) {
                onAddToSelectedItems(selectionStartValue);
            } else {
                onRemoveFromSelectedItems(selectionStartValue);
            }
        }

        this.setState({
            groupSelectStartValue: selectionStartValue,
        });
    };

    editableRowRenderer = ({ columnIndex, rowIndex, isScrolling, key, parent }) => {
        // isScrolling, isVisible
        const {
            catalog,
            viewNotes,
            activeList,
            bulkItems,
            onReorderEntries,
            editable,
            activeItemId,
            cart,
            isActiveList,
            activeBrand,
            onSnackbarAdd,
            app,
            onDrag,
            onDrop,
            scope,
            onEntriesRefresh,
            entities,
            bulkDragInsert,
            bulkDragReorder,
            results,
        } = this.props;
        const { viewDef, grid, View } = this.state;
        const { actionsOffset } = parent.props;

        const node = grid[rowIndex];

        if (!node) {
            // dont render things to the right of the label
            return null;
        }

        const style = {
            width: "100%",
            height: node.height,
            overflow: "hidden",
        };

        key = getNodeKey(node) ?? key;

        if (node.entry && node.entry.type === ITEM_SEPARATOR) {
            return (
                <SortableSeparator
                    app={app}
                    actions={this.itemActions}
                    key={key}
                    style={style}
                    index={node.index}
                    catalogEntry={node.entry}
                    editable={editable}
                    dnd="dropable"
                    onDragHover={onReorderEntries}
                    onBeginDrag={onDrag}
                    onEndDrag={onDrop}
                    onSnackbarAdd={onSnackbarAdd}
                    list={true}
                />
            );
        } else if (node.entry && node.entry.type === "image") {
            return (
                <SortableImage
                    actions={this.itemActions}
                    app={app}
                    catalogEntry={node.entry}
                    editable={editable}
                    index={node.index}
                    key={key}
                    iconIsSeparator={node.entry.id !== node.entry.sync_id}
                    onDragHover={onReorderEntries}
                    onBeginDrag={onDrag}
                    onEndDrag={onDrop}
                    onSnackbarAdd={onSnackbarAdd}
                    style={style}
                    scope={scope}
                    list={true}
                />
            );
        } else {
            const catalogDates = getCatalogDates(catalog);

            return (
                <View
                    app={app}
                    actions={this.itemActions}
                    activeBrand={activeBrand}
                    activeItemId={activeItemId}
                    activeList={activeList}
                    bulkItems={bulkItems}
                    cart={cart}
                    catalogEntry={node.entry}
                    catalog={catalog}
                    catalogDates={catalogDates}
                    editable={editable}
                    index={node.index}
                    isActiveList={isActiveList}
                    item={node.item}
                    list={true}
                    key={key}
                    onBeginDrag={onDrag}
                    onDragHover={onReorderEntries}
                    onEndDrag={onDrop}
                    placeholder={node.placeholder}
                    style={style}
                    view={viewDef}
                    viewNotes={viewNotes}
                    fullWidth={true}
                    onSnackbarAdd={onSnackbarAdd}
                    scope={scope}
                    actionsOffset={actionsOffset}
                    onEntriesRefresh={onEntriesRefresh}
                    entities={entities}
                    bulkDragInsert={bulkDragInsert}
                    bulkDragReorder={bulkDragReorder}
                    results={results}
                    touchDevice={app.touchDevice}
                />
            );
        }
    };

    rowRenderer = ({ rowIndex, key, parent }) => {
        const {
            viewNotes,
            bulkItems,
            activeList,
            activeItemId,
            cart,
            catalog,
            editable,
            isActiveList,
            activeBrand,
            app,
            onSnackbarAdd,
            scope,
            showActions,
            onEntriesRefresh,
            entities,
        } = this.props;
        const { viewDef, grid, View } = this.state;
        const { actionsOffset } = parent.props;

        const node = grid[rowIndex];

        if (!node) {
            // dont render things to the right of the label
            return null;
        }
        const style = {
            width: "100%",
            height: node.height,
            overflow: "hidden",
        };

        key = getNodeKey(node) ?? key;

        if (node.entry && node.entry.type === ITEM_SEPARATOR) {
            return (
                <Separator
                    key={key}
                    catalogEntry={node.entry}
                    style={style}
                    index={node.index}
                    onSnackbarAdd={onSnackbarAdd}
                    list={true}
                />
            );
        } else if (node.entry && node.entry.type === "image") {
            return (
                <Image
                    catalogEntry={node.entry}
                    key={key}
                    onSnackbarAdd={onSnackbarAdd}
                    style={style}
                    scope={scope}
                    list={true}
                />
            );
        } else {
            const catalogDates = getCatalogDates(catalog);

            return (
                <View
                    app={app}
                    actions={this.itemActions}
                    activeBrand={activeBrand}
                    activeItemId={activeItemId}
                    activeList={activeList}
                    bulkItems={bulkItems}
                    catalogDates={catalogDates}
                    cart={cart}
                    catalogEntry={node.entry}
                    catalog={catalog}
                    editable={editable}
                    isActiveList={isActiveList}
                    item={node.item}
                    key={key}
                    list={true}
                    style={style}
                    view={viewDef}
                    viewNotes={viewNotes}
                    fullWidth={true}
                    onSnackbarAdd={onSnackbarAdd}
                    schemas={entities.schemas}
                    scope={scope}
                    showActions={showActions}
                    actionsOffset={actionsOffset}
                    onEntriesRefresh={onEntriesRefresh}
                    entities={entities}
                />
            );
        }
    };

    handleWindowScrollEvent = () => {
        const { hasMoreResults, isFetching } = this.props;
        if (
            window.pageYOffset + window.innerHeight + 400 > document.documentElement.scrollHeight &&
            hasMoreResults &&
            !isFetching
        ) {
            this.props.onFetchNextPage();
        }
    };

    handleIsRowLoaded = ({ index }) => {
        return !!this.state.grid[index];
    };

    handleCalculateRowHeight = ({ index }) =>
        this.state.grid[index] ? this.state.grid[index].height : this.state.View.height;

    calculateActionFieldWidth = () => {
        const { me } = this.context;
        const { type, editable } = this.props;
        const { View } = this.state;
        let totalWidth = View.actionFieldWidth; // add to list
        if (me.hasPermission("can_order")) {
            // cart
            totalWidth += View.actionFieldWidth;
        }
        if (type === "catalog") {
            // notes
            totalWidth += View.actionFieldWidth;
        }
        if (type === "catalog" && editable) {
            // delete
            totalWidth += View.actionFieldWidth;
        }
        if (me.account_type !== AccountType.BUYER) {
            // edit
            totalWidth += View.actionFieldWidth;
        }
        return totalWidth;
    };

    noContentRenderer = () => {
        return this.props.isFetching ? null : this.props.noContentRenderer();
    };

    render() {
        const { editable, view, activeBrand, app, onSnackbarAdd, type, isFetching, results, scope, toolbarMinimized } =
            this.props;
        const { estimatedRows, height, View, viewDef } = this.state;

        const pricePerSize = activeBrand.hasPreference("price_per_size");

        const actionsWidth = this.calculateActionFieldWidth();

        let pricingColumns = 0;
        ["pricing", "msrp"].forEach((section) => {
            if (viewDef && viewDef.sections.get(section)) {
                pricingColumns += View.priceFieldWidth;
            }
        });
        pricingColumns = pricePerSize ? pricingColumns * 2 : pricingColumns;
        const rowWidth =
            (viewDef ? viewDef.getColumnWidths() : FALLBACK_ROW_WIDTH) +
            actionsWidth +
            pricingColumns +
            View.bulkSelectorFieldWidth;

        const styles = merge(
            {
                margin: "0 -5px 90px -5px",
                overflowX: "auto",
                minWidth: "100%",
                tableLayout: "fixed",
            },
            $(" noResults", {
                padding: "8px 0 0",
                height: "64px",
                lineHeight: "56px",
                fontSize: "20px",
                textAlign: "center",
            }),
            $(" .hover", {
                position: "absolute",
                width: `${actionsWidth}px`,
            }),
        );

        const listStyle = {
            overflowX: "auto" as "auto",
            minWidth: "100%",
            tableLayout: "fixed" as "fixed",
            overflowY: "hidden" as "hidden",
        } as any;

        return (
            <div {...styles}>
                <ScrollSync>
                    {({ onScroll, scrollHeight, scrollLeft }) => {
                        return (
                            <AutoSizer disableHeight={true}>
                                {({ width }) => {
                                    if (width === 0) {
                                        return null;
                                    }
                                    const gridProps = {
                                        actionsOffset: width + scrollLeft - actionsWidth,
                                    };
                                    return (
                                        <div>
                                            {editable && app.touchDevice && (
                                                <DragPreview
                                                    view={view}
                                                    activeBrand={activeBrand}
                                                    app={app}
                                                    onSnackbarAdd={onSnackbarAdd}
                                                    type={type}
                                                    scope={scope}
                                                    list={true}
                                                    editable={editable}
                                                    actionsOffset={gridProps.actionsOffset}
                                                />
                                            )}
                                            <Grid
                                                {...gridProps}
                                                noContentRenderer={this.noContentRenderer}
                                                cellRenderer={editable ? this.editableRowRenderer : this.rowRenderer}
                                                rowHeight={this.handleCalculateRowHeight}
                                                rowCount={
                                                    results.length === 0 && !isFetching && toolbarMinimized
                                                        ? 0
                                                        : estimatedRows
                                                }
                                                height={
                                                    results.length === 0 && !isFetching && toolbarMinimized
                                                        ? NO_RESULTS_GRID_HEIGHT
                                                        : height || View.height
                                                }
                                                width={width}
                                                columnWidth={rowWidth < width ? width : rowWidth}
                                                style={listStyle}
                                                onScroll={onScroll}
                                                columnCount={
                                                    results.length === 0 && !isFetching && toolbarMinimized ? 0 : 1
                                                }
                                                scrollLeft={scrollLeft}
                                            />
                                        </div>
                                    );
                                }}
                            </AutoSizer>
                        );
                    }}
                </ScrollSync>
                {isFetching && (
                    <div style={{ padding: "60px 0", textAlign: "center" }}>
                        <CircularProgress />
                    </div>
                )}
            </div>
        );
    }
}
